import { Box } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  createBrowserRouter,
  MemoryRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router';
import Topbar from '@/components/Organisms/Topbar';
import BenefitsVerification from '@/pages/BenefitsVerification';
import Dashboard from '@/pages/Dashboard';
import ProfilePage from '@/pages/Profile';
import { Provider as ChakraUIProvider } from './components/ui/provider';
import '@/styles/global.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import NotificationSSEListener from '@/components/Organisms/NotificationSSEListener';
import TourHandler from '@/components/Organisms/TourHandler';
import { AuditTable } from '@/components/Templates/Audit';
import ErrorBoundary from '@/components/Templates/ErrorBoundary';
import RouteErrorHandler from '@/components/Templates/ErrorBoundary/RouteErrorHandler';
import { WithPageConfig } from '@/components/Templates/WithPageConfig';
import AppCrashProvider from '@/components/ui/AppCrashProvider';
import AuthProvider from '@/components/ui/AuthProvider';
import { NotificationProvider } from '@/components/ui/notification-provider';
import { environments, permissionToUrl } from '@/constants';
import { useUpdateTutorialsRemaining } from '@/hooks/service-hooks/tutorials';
import usePermission from '@/hooks/usePermission';
import { usePageAnalytics } from '@/hooks/usePageAnalytics';
import AppCrashReportsPage from '@/pages/AppCrashReport';
import ChoosePlatformPage from '@/pages/ChoosePlatform';
import ConsignmentOrders from '@/pages/ConsignmentOrders';
import Documents from '@/pages/Documents';
import Error401 from '@/pages/Error401';
import Error402 from '@/pages/Error402';
import Error403 from '@/pages/Error403';
import Error404 from '@/pages/Error404';
import ForgotPasswordPage from '@/pages/ForgotPassword';
import Home from '@/pages/Home';
import LoginPage from '@/pages/Login';
import ManagePatients from '@/pages/ManagePatients';
import NotificationDetails from '@/pages/NotificationDetails';
import Notifications from '@/pages/Notifications';
import OpenInvoices from '@/pages/OpenInvoices';
import OrderReturns from '@/pages/OrderReturns';
import Requests from '@/pages/Requests';
import { SupportTickets } from '@/pages/SupportTickets';
import { selectTutorialRemaining, useAuthStore } from '@/store';
import { TourProvider } from '@/store/tourContext';
import { type NavItem, navigationItems } from '@/utils/navigationItems';
import {
  type ProfileMenuItem,
  profileMenuItems,
} from '@/utils/profileMenuItems';
import FaceliftOverlayLoader from './components/Atoms/FaceliftOverlayLoader/FaceliftOverlayLoader';
import { ActivityMointer } from './components/Molecules/ActivityMointer';
import { BackgroundProcess } from './components/Molecules/BackgroundProcess';
import MisbilledHCPCSTable from './components/Templates/BillingTools/MisbilledHCPCS';
import MisbilledUnitsTable from './components/Templates/BillingTools/MisbilledUnits';
import UnmatchedClaimstoinvoicesTable from './components/Templates/BillingTools/UnmatchedClaimstoinvoices';
import UnmatchedInvoicestoClaimsTable from './components/Templates/BillingTools/UnmatchedInvoicestoClaims';
import InvoicePaymentLockTable from './components/Templates/InvoicePaymentLock/InvoicePaymentLockTable';
import ReleaseProvider from './components/ui/ReleaseProvider';
import ToasterProvider from './components/ui/toaster';
import AllInvoice from './pages/AllInvoice';
import Collections from './pages/Collections';
import DenialSupportPage from './pages/DenialSupport/DenialSupportPage';
import InvoiceCart from './pages/InvoiceCart';
import { ManageUsersRolesPage } from './pages/ManageUsersRoles';
import PaidInvoice from './pages/PaidInvoice';
import Drafts from './pages/Drafts';
import Test from './pages/Test';
import { ConfirmedGrafts } from './pages/Utilities/ConfirmedGrafts';
import { GraftsToAssign } from './pages/Utilities/GraftsToAssign';
import { initializeAnalytics, syncAnalyticsUser } from './utils/analytics';
import { closeAllOpenComponents } from './utils/tutorials/tutorialsUtils';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: 1000 * 60 * 60, // 1 hour in ms
      // cacheTime: 1000 * 60 * 60, // 1 hour in ms
      refetchOnWindowFocus: false, // Disables automatic refetching when browser window is focused.
      retry: 0,
    },
  },
});

// Protected Route Guard - redirects to login if not authenticated
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login?logout=true" replace />;
  }

  if (user?.userInformation?.isFirstLogin && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Layout component that includes the topbar and loading logic
const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const user = useAuthStore((state) => state.user);
  const userId = user?.userInformation?._id || null;
  const tutorialRemaining = useAuthStore(selectTutorialRemaining);
  const { hasPermission } = usePermission();
  const prevPathnameRef = useRef<string>(location.pathname);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialRouteRef = useRef<string | null>(null);

  usePageAnalytics();

  const { mutate: updateTutorialsRemaining } = useUpdateTutorialsRemaining(
    (data) => {
      // Update the local state with the new tutorialRemaining value
      const authStore = useAuthStore.getState();
      authStore.updateUserInformation({
        tutorialRemaining: data.tutorialsRemaining,
      });
    },
    (error) => {
      console.error('Failed to update tutorials remaining:', error);
      toast.error('Failed to update tutorial progress');
    },
  );

  // Handle tour completion or skip
  const handleTourEnd = useCallback(
    ({
      reason,
      tutorialName,
      startedManually,
    }: {
      reason: 'completed' | 'skipped';
      tutorialName: string;
      startedManually?: boolean;
    }) => {
      // Close all open components when tutorial ends (completed or skipped)
      closeAllOpenComponents();

      // Redirect to tutorials tab in profile if tutorial was started manually and completed
      if (startedManually && reason === 'completed') {
        navigate('/profile?tab=tutorials');
      }

      // Handle onboarding tutorials logic (OnboardingUser, OnboardingWCTutorial, OnboardingSalesRepTutorial, OnboardingTutorialGraftLogs, OnboardingTutorialPaymentMSL, OnboardingMindsightTutorial)
      const isOnboardingTutorialName =
        tutorialName === 'OnboardingUser' ||
        tutorialName === 'OnboardingWCTutorial' ||
        tutorialName === 'OnboardingSalesRepTutorial' ||
        tutorialName === 'OnboardingTutorialGraftLogs' ||
        tutorialName === 'OnboardingTutorialPaymentMSL' ||
        tutorialName === 'OnboardingMindsightTutorial';

      if (
        !isOnboardingTutorialName ||
        !userId ||
        tutorialRemaining === undefined
      ) {
        return;
      }
			const targetRoute = initialRouteRef.current || '/';

      // When tour is completed, set to 0 if tutorialRemaining > 0
      if (reason === 'completed') {
        if (tutorialRemaining > 0) {
          updateTutorialsRemaining({
            userId,
            value: 0,
          });     
          navigate(targetRoute, { replace: true });
        }
        return;
      }

      // When tour is skipped, reduce by 1
      const newValue = Math.max(0, (tutorialRemaining ?? 0) - 1);

      if (tutorialRemaining === newValue) {
        return;
      }

      updateTutorialsRemaining({
        userId,
        value: newValue,
      });
      navigate(targetRoute, { replace: true });
    },
    [userId, tutorialRemaining, updateTutorialsRemaining],
  );

  // Store the first available route before onboarding tutorial starts
  useEffect(() => {
    if (
      tutorialRemaining !== undefined &&
      tutorialRemaining > 0 &&
      !initialRouteRef.current &&
      user?.userInformation?._id
    ) {
      const firstMatchedRouteAsPerPermission = Object.entries(permissionToUrl).find(
        ([permission]) => {
          return hasPermission([permission]);
        },
      );

      if (firstMatchedRouteAsPerPermission) {
        initialRouteRef.current = firstMatchedRouteAsPerPermission[1];
      } else if (hasPermission(['Dashboard:Visualize'])) {
        initialRouteRef.current = '/';
      } else {
        initialRouteRef.current = '/profile';
      }
    }
  }, [tutorialRemaining, hasPermission, user?.userInformation?._id]);

  useEffect(() => {
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    
    // exlide TA loader if user navigating inside folder for other routes it will show loader
    if (pathnameChanged) {
      if (
        location.pathname !== '/documents' ||
        (location.pathname === '/documents' && location.search === '')
      ) {
        setIsLoading(true);
      }
    } else {
      // If pathname didn't change (only search params changed), ensure loading is false
      setIsLoading(false);
    }

    prevPathnameRef.current = location.pathname;

    if (!user?.userInformation || !user?.userInformation?._id) {
      navigate('/login', { replace: true });
      return;
    }

    // For root path, check if user has Dashboard permission
    // If not, redirect to their first available route
		if (location.pathname === '/'  && !hasPermission(['Dashboard:Visualize'])) {
			const firstMatchedRouteAsPerPermission = Object.entries(permissionToUrl).find(([permission]) => {
				return hasPermission([permission]);
			});

      if (firstMatchedRouteAsPerPermission) {
        navigate(firstMatchedRouteAsPerPermission[1], { replace: true });
        return;
      }
    }

    const findNavigationItem = (
      items: NavItem[],
      pathname: string,
    ): NavItem | undefined => {
      for (const item of items) {
        if (item.href === pathname) {
          return item;
        }
        if (item.submenu) {
          const found = findNavigationItem(item.submenu, pathname);
          if (found) {
            return found;
          }
        }
      }
      return undefined;
    };

    const findProfileMenuItem = (
      items: ProfileMenuItem[],
      pathname: string,
    ): ProfileMenuItem | undefined => {
      for (const item of items) {
        if (item.href === pathname) {
          return item;
        }
        if (item.submenu) {
          const found = findProfileMenuItem(item.submenu, pathname);
          if (found) {
            return found;
          }
        }
      }
      return undefined;
    };

    const navigationItem = findNavigationItem(
      navigationItems,
      location.pathname,
    );

    const profileMenuItem = findProfileMenuItem(
      profileMenuItems,
      location.pathname,
    );

    const locationDetails = navigationItem || profileMenuItem;

    if (
      locationDetails?.permissions?.length &&
      !hasPermission(locationDetails.permissions)
    ) {
      navigate('/error-403', { replace: true });
      return;
    }
    
    if (pathnameChanged) {
      loadingTimeoutRef.current = setTimeout(() => setIsLoading(false), 1000);
    }

    // Cleanup function to clear timeout
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [location.pathname, location.search, user?.userInformation, navigate, hasPermission]);

  return (
    <TourProvider autoStart={false} onTourEnd={handleTourEnd}>
      <TourHandler />
      <Box minH="100vh" bg="brand.pageBackground" display="flex" flexDirection="column">
        {isLoading && <FaceliftOverlayLoader />}
        <Topbar />
        <ActivityMointer />
        <Box as="main" flex="1" bg="brand.pageBackground" minH="0">
          <Outlet />
        </Box>
      </Box>
    </TourProvider>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorHandler />,
    children: [
      {
        index: true,
        element: (
          <WithPageConfig path="/">
            <Home />
            {/* <Dashboard /> */}
          </WithPageConfig>
        ),
      },
      // {
      //   path: 'home',
      //   element: (
      //     <WithPageConfig path="/home">
      //       <Home />
      //     </WithPageConfig>
      //   ),
      // },
      {
        path: 'benefits-verifications',
        element: (
          <WithPageConfig path="/benefits-verifications">
            <BenefitsVerification />
          </WithPageConfig>
        ),
      },
      {
        path: 'profile',
        element: (
          <WithPageConfig path="/profile">
            <ProfilePage />
          </WithPageConfig>
        ),
      },
      {
        path: 'open-invoices',
        element: (
          <WithPageConfig path="/open-invoices">
            <OpenInvoices />
          </WithPageConfig>
        ),
      },
      {
        path: 'misbilled-hcpcs',
        element: (
          <WithPageConfig path="/misbilled-hcpcs">
            <MisbilledHCPCSTable />
          </WithPageConfig>
        ),
      },
      {
        path: 'misbilled-units',
        element: (
          <WithPageConfig path="/misbilled-units">
            <MisbilledUnitsTable />
          </WithPageConfig>
        ),
      },
      {
        path: 'unmatched-claims-to-invoices',
        element: (
          <WithPageConfig path="/unmatched-claims-to-invoices">
            <UnmatchedClaimstoinvoicesTable />
          </WithPageConfig>
        ),
      },
      {
        path: 'unmatched-invoices-to-claim',
        element: (
          <WithPageConfig path="/unmatched-invoices-to-claim">
            <UnmatchedInvoicestoClaimsTable />
          </WithPageConfig>
        ),
      },
      {
        path: 'paid-invoices',
        element: (
          <WithPageConfig path="/paid-invoices">
            <PaidInvoice />
          </WithPageConfig>
        ),
      },
      {
        path: 'all-invoices',
        element: (
          <WithPageConfig path="/all-invoices">
            <AllInvoice />
          </WithPageConfig>
        ),
      },
      {
        path: 'invoice-cart',
        element: (
          <WithPageConfig path="/invoice-cart">
            <InvoiceCart />
          </WithPageConfig>
        ),
      },
      {
        path: 'test',
        element: (
          <WithPageConfig path="/test">
            <Test />
          </WithPageConfig>
        ),
      },
      {
        path: 'manage-patients',
        element: (
          <WithPageConfig path="/manage-patients">
            <ManagePatients />
          </WithPageConfig>
        ),
      },
      {
        path: 'returns',
        element: (
          <WithPageConfig path="/returns">
            <OrderReturns />
          </WithPageConfig>
        ),
      },
      {
        path: 'requests',
        element: (
          <WithPageConfig path="/requests">
            <Requests />
          </WithPageConfig>
        ),
      },
      {
        path: 'drafts',
        element: (
          <WithPageConfig path="/drafts">
            <Drafts />
          </WithPageConfig>
        ),
      },
      {
        path: 'consignments',
        element: (
          <WithPageConfig path="/consignments">
            <ConsignmentOrders />
          </WithPageConfig>
        ),
      },
      {
        path: 'manage-users',
        element: (
          <WithPageConfig path="/manage-users">
            <ManageUsersRolesPage />
          </WithPageConfig>
        ),
      },
      {
        path: 'notifications',
        element: (
          <WithPageConfig path="/notifications">
            <Notifications />
          </WithPageConfig>
        ),
      },
      {
        path: 'notifications/:notificationId',
        element: (
          <WithPageConfig path="/notifications/:notificationId">
            <NotificationDetails />
          </WithPageConfig>
        ),
      },
      {
        path: 'grafts-assign',
        element: (
          <WithPageConfig path="/grafts-assign">
            <GraftsToAssign />
          </WithPageConfig>
        ),
      },
      {
        path: 'grafts-confirmed',
        element: (
          <WithPageConfig path="/grafts-confirmed">
            <ConfirmedGrafts />
          </WithPageConfig>
        ),
      },
      {
        path: 'audit',
        element: (
          <WithPageConfig path="/audit">
            <AuditTable />
          </WithPageConfig>
        ),
      },
      {
        path: 'payment-locks',
        element: (
          <WithPageConfig path="/payment-locks">
            <InvoicePaymentLockTable />
          </WithPageConfig>
        ),
      },
      {
        path: 'billing-tools',
        element: (
          <WithPageConfig path="/billing-tools">
            <></>
          </WithPageConfig>
        ),
      },
      {
        path: 'collections',
        element: (
          <WithPageConfig path="/collections">
            <Collections />
          </WithPageConfig>
        ),
      },
      {
        path: 'denial-support',
        element: (
          <WithPageConfig path="/denial-support">
            <DenialSupportPage />
          </WithPageConfig>
        ),
      },
      {
        path: 'reports/finance',
        element: (
          <WithPageConfig path="/reports/finance">
            <></>
          </WithPageConfig>
        ),
      },
      {
        path: 'reports/daily-cash-flow',
        element: (
          <WithPageConfig path="/reports/daily-cash-flow">
            <></>
          </WithPageConfig>
        ),
      },
      {
        path: 'documents',
        element: (
          <WithPageConfig path="/documents">
            <Documents />
          </WithPageConfig>
        ),
      },
      {
        path: 'support',
        element: (
          <WithPageConfig path="/support">
            <SupportTickets />
          </WithPageConfig>
        ),
      },
      {
        path: 'app-crash-logs',
        element: (
          <WithPageConfig path="/app-crash-logs">
            <AppCrashReportsPage />
          </WithPageConfig>
        ),
      },
      {
        path: '/error-boundary',
        element: <ErrorBoundary />,
      },
    ],
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/choose-platform',
    element: (
      <ProtectedRoute>
        <ChoosePlatformPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/error-401',
    element: <Error401 />,
  },
  {
    path: '/error-402',
    element: <Error402 />,
  },
  {
    path: '/error-403',
    element: <Error403 />,
  },
  {
    path: '/error-404',
    element: <Error404 />,
  },
  {
    path: '*',
    element: <Error404 />,
  },
]);

const App = () => {
  const user = useAuthStore((state) => state.user);
  const userId = user?.userInformation?._id || null;
  const notificationsEnabled =
    user?.userInformation?.notificationsEnabled || false;

  // Configure Sentry user + tags when auth state changes
  useEffect(() => {
    initializeAnalytics();
    syncAnalyticsUser(user || null);
  }, [user]);

  useEffect(() => {
    if (!user || !user.userInformation) {
      Sentry.setUser(null);
      return;
    }

    const userInfo = user.userInformation;

    Sentry.setUser({
      email: userInfo.email,
      username: `${userInfo.firstName} ${userInfo.lastName}`,
      ip_address: '{{auto}}',
    });

    const auditUser = JSON.parse(localStorage.getItem('auditUser') || '{}');
    Sentry.setTags({
      environment:
        environments[import.meta.env.VITE_ENV as keyof typeof environments] ||
        'QA',
      city: auditUser.city || '',
      state: auditUser.state || '',
      country: auditUser.country || '',
      port: userInfo.portsAvailable || '',
      user_type: userInfo.isExternal ? 'External' : 'Internal',
      user_agent: navigator.userAgent,
    });
  }, [user]);

  return (
    <ChakraUIProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ReleaseProvider userId={userId} />
          <AppCrashProvider userId={userId} />
          <MemoryRouter>
            <BackgroundProcess userId={userId} />
          </MemoryRouter>
          <NotificationProvider enabled={notificationsEnabled} userId={userId}>
            <NotificationSSEListener />
            <RouterProvider router={router} />
            <ToasterProvider />
          </NotificationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ChakraUIProvider>
  );
};

export default App;
